import { Box, rem } from "@mantine/core"
import { type NotificationData, notifications } from "@mantine/notifications"
import { IconAlertTriangle, IconCheck, IconExclamationCircle, IconInfoCircle, IconQuestionMark } from "@tabler/icons-react"

const iconSize = rem(16)

interface IShowNotificationProps extends NotificationData {
  type?: "success" | "info" | "warning" | "error"
}

/**
 * Displays a notification with a specified type, message, and additional properties.
 *
 * It is a wrapper around the `notifications.show` function from Mantine with some extras:
 * - It is able to show clickable links in the message text.
 * - It has an extra optional prop for the message type, allowing to show different icons and colors based on the type.
 *
 * @param {IShowNotificationProps} props - The properties for the notification.
 * @param {string} [props.type="info"] - The type of the notification. Can be "success", "info", "warning", or "error". It is optional and defaults to "info".
 * @param {string} props.message - The message to display in the notification. It can contain HTML (including clickable links).
 * @param {object} [props.icon] - The icon to display in the notification.
 * @param {string} [props.bgColor] - The background color of the notification.
 * @param {object} [props.rest] - Additional properties to pass to the notification.
 *
 * @returns {void}
 */
export function showNotification({ type = "info", message, ...props }: IShowNotificationProps) {
  let { icon, bgColor } = {
    icon: <IconQuestionMark style={{ width: iconSize, height: iconSize }} />,
    bgColor: "gray",
  }

  switch (type) {
    case "success":
      icon = <IconCheck style={{ width: iconSize, height: iconSize }} />
      bgColor = "green"
      break
    case "info":
      icon = <IconInfoCircle style={{ width: iconSize, height: iconSize }} />
      bgColor = "blue"
      break
    case "warning":
      icon = <IconAlertTriangle style={{ width: iconSize, height: iconSize }} />
      bgColor = "yellow"
      break
    case "error":
      icon = <IconExclamationCircle style={{ width: iconSize, height: iconSize }} />
      bgColor = "red"
      break
    default:
      break
  }

  return notifications.show({
    withBorder: true,
    ...props,
    icon,
    color: bgColor,
    message: (
      <Box
        className="notificationWithLinks"
        dangerouslySetInnerHTML={{
          __html: String(message ?? ""),
        }}
      />
    ),
  })
}

export function updateNotification({
  id,
  type,
  message,
  ...props
}: Omit<Partial<IShowNotificationProps>, "message"> & {
  id: string
  message: React.ReactNode
}) {
  let { icon, bgColor } = {
    icon: <IconQuestionMark style={{ width: iconSize, height: iconSize }} />,
    bgColor: "gray",
  }

  let updateIcon = false

  if (type) {
    switch (type) {
      case "success":
        icon = <IconCheck style={{ width: iconSize, height: iconSize }} />
        bgColor = "green"
        break
      case "info":
        icon = <IconInfoCircle style={{ width: iconSize, height: iconSize }} />
        bgColor = "blue"
        break
      case "warning":
        icon = <IconAlertTriangle style={{ width: iconSize, height: iconSize }} />
        bgColor = "yellow"
        break
      case "error":
        icon = <IconExclamationCircle style={{ width: iconSize, height: iconSize }} />
        bgColor = "red"
        break
      default:
        break
    }
    updateIcon = true
  }

  notifications.update({
    id,
    ...(updateIcon
      ? {
          icon,
          color: bgColor,
        }
      : {}),
    ...props,
    message: (
      <Box
        className="notificationWithLinks"
        dangerouslySetInnerHTML={{
          __html: String(message ?? ""),
        }}
      />
    ),
  })
}
